import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { brand, radius } from 'utils/designTokens';

const dialogPaperSx = {
  borderRadius: `${radius.md}px`,
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
};

const headerSx = {
  bgcolor: brand.primary,
  color: 'common.white',
  m: 0,
  px: 3,
  py: 2.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2
};

const contentSx = {
  px: 3,
  py: 3,
  bgcolor: 'grey.50'
};

const footerSx = {
  px: 3,
  py: 2,
  bgcolor: 'background.paper',
  borderTop: '1px solid',
  borderColor: 'divider',
  gap: 1.5
};

const sectionSx = {
  p: 2.5,
  borderRadius: `${radius.md}px`,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper'
};

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'text.primary',
  letterSpacing: '0.01em'
};

const emptyStateSx = {
  py: 2,
  px: 1.5,
  textAlign: 'center',
  borderRadius: `${radius.sm}px`,
  border: '1px dashed',
  borderColor: 'divider',
  bgcolor: 'grey.50'
};

const storeChipSx = {
  mt: 1,
  height: 24,
  fontWeight: 500,
  fontSize: '0.7rem',
  bgcolor: 'rgba(255,255,255,0.12)',
  color: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(255,255,255,0.2)',
  '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
  '& .MuiChip-label': { px: 0.75 }
};

// ==============================|| ENTITY FORM DIALOG ||============================== //

export function EntityFormDialog({ open, onClose, maxWidth = 'md', children }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
    >
      {children}
    </Dialog>
  );
}

EntityFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  maxWidth: PropTypes.string,
  children: PropTypes.node
};

export function FormDialogHeader({ icon, title, storeName, onClose }) {
  return (
    <DialogTitle sx={headerSx}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
        {icon ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'common.white',
              opacity: 0.95,
              '& svg': { fontSize: 24 }
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, py: 0.25 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: 'inherit', lineHeight: 1.35, mb: 0 }}>
            {title}
          </Typography>
          {storeName ? (
            <Chip
              icon={<StorefrontRoundedIcon sx={{ fontSize: '14px !important' }} />}
              label={storeName}
              size="small"
              sx={storeChipSx}
            />
          ) : null}
        </Box>
      </Stack>
      <IconButton
        onClick={onClose}
        size="small"
        aria-label="Close"
        sx={{
          color: 'common.white',
          ml: 0.5,
          flexShrink: 0,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
  );
}

FormDialogHeader.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  storeName: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

export function FormValidationAlert({ message, severity = 'error' }) {
  if (!message) return null;
  return (
    <Alert severity={severity} variant="outlined" sx={{ mb: 2, borderRadius: `${radius.sm}px` }}>
      {message}
    </Alert>
  );
}

FormValidationAlert.propTypes = {
  message: PropTypes.string,
  severity: PropTypes.string
};

export function FormDialogContent({ children, loading, formError }) {
  return (
    <DialogContent dividers sx={contentSx}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={32} sx={{ color: brand.primary }} />
        </Box>
      ) : (
        <>
          <FormValidationAlert message={formError} />
          {children}
        </>
      )}
    </DialogContent>
  );
}

FormDialogContent.propTypes = {
  children: PropTypes.node,
  loading: PropTypes.bool,
  formError: PropTypes.string
};

const formSectionAddButtonSx = {
  flexShrink: 0,
  borderRadius: 20,
  fontWeight: 600,
  fontSize: '0.8125rem',
  lineHeight: 1,
  px: 1.75,
  py: 0.75,
  minHeight: 32,
  textTransform: 'none',
  '& .MuiButton-startIcon': {
    marginRight: 0.5,
    marginLeft: -0.25,
    display: 'inline-flex',
    alignItems: 'center',
    '& svg': {
      display: 'block'
    }
  }
};

export function FormSectionAddButton({ children, onClick, ...rest }) {
  return (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
      onClick={onClick}
      sx={formSectionAddButtonSx}
      {...rest}
    >
      {children}
    </Button>
  );
}

FormSectionAddButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired
};

export function FormSection({ title, action, children }) {
  return (
    <Box sx={sectionSx}>
      {(title || action) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 1.5 }}>
          {title ? <Typography sx={sectionTitleSx}>{title}</Typography> : <Box />}
          {action}
        </Stack>
      )}
      {children}
    </Box>
  );
}

FormSection.propTypes = {
  title: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node
};

export function FormDialogFooter({ onCancel, onSubmit, submitting, submitLabel, cancelLabel = 'Cancel' }) {
  return (
    <DialogActions sx={footerSx}>
      <Button onClick={onCancel} disabled={submitting} color="inherit" sx={{ color: 'text.secondary' }}>
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        onClick={onSubmit}
        disabled={submitting}
        sx={{
          borderRadius: `${radius.sm}px`,
          px: 2.5,
          fontWeight: 600,
          bgcolor: brand.primary,
          boxShadow: 'none',
          '&:hover': { bgcolor: brand.primaryDark, boxShadow: 'none' }
        }}
      >
        {submitting ? 'Saving…' : submitLabel}
      </Button>
    </DialogActions>
  );
}

FormDialogFooter.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  submitLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string
};

export function FormEmptyState({ children }) {
  return (
    <Box sx={emptyStateSx}>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}

FormEmptyState.propTypes = { children: PropTypes.node };

/** Compact inline image picker — thumbnail + upload button */
export function FormImageUpload({ label, preview, placeholderIcon, hint, hasFile, onFileChange }) {
  return (
    <Box
      sx={{
        mt: label ? 2.5 : 0,
        pt: label ? 2.5 : 0,
        borderTop: label ? '1px solid' : 'none',
        borderColor: 'divider'
      }}
    >
      {label ? (
        <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>{label}</Typography>
      ) : null}
      <Stack direction="row" spacing={2.5} alignItems="center">
        <Box
          sx={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: `${radius.sm}px`,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100'
          }}
        >
          {preview ? (
            <Box component="img" src={preview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ color: 'text.disabled', display: 'flex', '& svg': { fontSize: 28 } }}>{placeholderIcon}</Box>
          )}
        </Box>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Button
            variant="outlined"
            size="small"
            component="label"
            sx={{
              alignSelf: 'flex-start',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { borderColor: brand.primary, color: brand.primary, bgcolor: 'transparent' }
            }}
          >
            {hasFile || preview ? 'Change image' : 'Upload image'}
            <input type="file" hidden accept="image/*" onChange={onFileChange} />
          </Button>
          {hint ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {hint}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

FormImageUpload.propTypes = {
  label: PropTypes.string,
  preview: PropTypes.string,
  placeholderIcon: PropTypes.node,
  hint: PropTypes.string,
  hasFile: PropTypes.bool,
  onFileChange: PropTypes.func.isRequired
};
